import { isThicknessPresetSelected } from "@plaatplan/core";
import { useLocale } from "../i18n/useLocale.js";
import { LengthInput } from "./LengthInput";

function thicknessOptions(presets, valueMm) {
  const values = [...presets];
  const current = Number(valueMm);
  if (Number.isFinite(current) && current > 0 && !values.includes(current)) {
    values.push(current);
    values.sort((a, b) => a - b);
  }
  return values;
}

/**
 * Thickness input with optional quick presets (card) or compact select (table).
 */
export function ThicknessField({
  valueMm,
  onChangeMm,
  presets,
  quickPresets,
  variant = "card",
  className = "",
  ariaLabel,
  presetsAriaLabel,
}) {
  const { display } = useLocale();
  const dimensionUnit = display.unitSystem === "imperial"
    ? display.t("web.fields.unitIn")
    : display.t("web.fields.unitMm");
  const isImperial = display.unitSystem === "imperial";
  const numericValue = Number(valueMm);
  const snapPresets = presets;
  const buttonPresets = quickPresets ?? presets;

  if (variant === "table") {
    const options = thicknessOptions(presets, valueMm);
    const useCustomInput = Number.isFinite(numericValue)
      && numericValue > 0
      && !presets.includes(numericValue);

    if (useCustomInput) {
      return (
        <LengthInput
          compact
          className={`table-input thickness-input${className ? ` ${className}` : ""}`}
          valueMm={valueMm}
          minMm={1}
          step={1}
          snapMmValues={snapPresets}
          aria-label={ariaLabel}
          onChangeMm={(value) => onChangeMm(Number(value))}
        />
      );
    }

    return (
      <select
        className={`table-input thickness-input${className ? ` ${className}` : ""}`}
        value={numericValue}
        aria-label={ariaLabel}
        onChange={(event) => onChangeMm(Number(event.target.value))}
      >
        {options.map((thickness) => (
          <option key={thickness} value={thickness}>
            {display.formatThickness(thickness)}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className={`source-thickness-row${isImperial ? " source-thickness-row--imperial" : ""}`}>
      <span className="source-thickness-label">{ariaLabel}</span>
      <label className="source-thickness-input">
        <div className="number-field compact">
          <LengthInput
            compact
            className={`table-input dimension-input${className ? ` ${className}` : ""}`}
            valueMm={valueMm}
            minMm={1}
            step={1}
            snapMmValues={snapPresets}
            aria-label={ariaLabel}
            onChangeMm={(value) => onChangeMm(Number(value))}
          />
          <em>{dimensionUnit}</em>
        </div>
      </label>
      <div
        className="source-thickness-presets"
        role="group"
        aria-label={presetsAriaLabel}
      >
        {buttonPresets.map((value) => {
          const selected = isThicknessPresetSelected(
            numericValue,
            value,
            snapPresets,
            display.unitSystem,
          );
          return (
            <button
              key={value}
              type="button"
              className={`thickness-preset-btn${selected ? " selected" : ""}`}
              aria-pressed={selected}
              onClick={() => onChangeMm(value)}
            >
              {display.formatLengthInput(value)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
